/**
 * NiceSelect — випадний список у стилі проєкту (ч.965, зауваження власника:
 * «дуже допотопно виглядають такі випадашки і в нічному, і в денному режимі»).
 *
 * ЧОМУ ВЗАГАЛІ ТРЕБА. Розкритий список нативного `<select>` малює САМА
 * операційна система: його не можна оформити ні CSS, ні змінними теми — тому
 * він не знає про нічний режим і виглядає чужорідно в обох темах.
 *
 * ЯК ЦЕ ПРАЦЮЄ (важливо для наступного агента). Компонент НЕ вимагає
 * переписувати 707 наявних `<select>` по сторінках: у `main.tsx` він
 * підміняє рендер `select` глобально (див. `installNiceSelect`). Сам
 * `<select>` лишається у DOM — прихований, але живий: на ньому як і раніше
 * висять `value`/`onChange`/`name`/валідація форми, а `<option>`-и
 * читаються з нього ж. Тому:
 *   • логіка сторінок не змінюється (жодних правок у 242 файлах);
 *   • тести, що шукають `<select>`/`option`, продовжують працювати;
 *   • якщо компонент колись прибрати — все повернеться до нативного вигляду.
 *
 * Що вміє: клавіатура (стрілки/Enter/Esc/Home/End, набір символів для
 * переходу), пошук для довгих списків (від `SEARCH_FROM` опцій), групи
 * `<optgroup>`, disabled-опції, вибір поточного значення, портал у body
 * (щоб список не обрізався попапом/таблицею з overflow).
 *
 * НЕ підміняємо: `multiple` і `size>1` — це вже не випадашка, а список;
 * нативна поведінка там доречна (у проєкті таких одиниці).
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/** Від скількох опцій показувати рядок пошуку. */
const SEARCH_FROM = 12;

interface Opt {
  value: string;
  label: string;
  disabled: boolean;
  group: string | null;
}

/** Зчитати опції з реального `<select>` (джерело істини — він). */
function readOptions(el: HTMLSelectElement): Opt[] {
  const out: Opt[] = [];
  for (const node of Array.from(el.children)) {
    if (node instanceof HTMLOptGroupElement) {
      for (const o of Array.from(node.children)) {
        if (o instanceof HTMLOptionElement) {
          out.push({
            value: o.value, label: o.textContent ?? '',
            disabled: o.disabled, group: node.label,
          });
        }
      }
    } else if (node instanceof HTMLOptionElement) {
      out.push({ value: node.value, label: node.textContent ?? '', disabled: node.disabled, group: null });
    }
  }
  return out;
}

interface Props {
  /** Прихований нативний select — джерело опцій і приймач значення. */
  selectRef: React.RefObject<HTMLSelectElement | null>;
  value: string;
  disabled: boolean;
  /** Версія: змінюється, коли options у DOM оновились. */
  version: number;
  onPick: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  title?: string;
  id?: string;
  /** Вигляд-обгортка: озвучує нативний select, тому себе ховаємо. */
  ariaHidden?: boolean;
}

export default function NiceSelect({
  selectRef, value, disabled, version, onPick, className, style, title, id,
  ariaHidden,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hi, setHi] = useState(0);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const typeBuf = useRef({ text: '', at: 0 });
  const [pos, setPos] = useState<{ top: number; left: number; width: number; drop: 'down' | 'up' }>(
    { top: 0, left: 0, width: 0, drop: 'down' });
  const listId = useId();

  const options = useMemo(
    () => (selectRef.current ? readOptions(selectRef.current) : []),
    // version змінюється, коли сторінка перемалювала <option> (напр. після
    // завантаження довідника) — тоді перечитуємо.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version, open, selectRef],
  );

  const selected = options.find((o) => o.value === value) ?? null;
  const withSearch = options.length >= SEARCH_FROM;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    // Збіг і з назвою групи (ч.1068): у шапці організації згруповані по
    // клієнтах — пошук за назвою клієнта показує всі його організації.
    return options.filter((o) => o.label.toLowerCase().includes(q)
      || (o.group ?? '').toLowerCase().includes(q));
  }, [options, query]);

  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);

  const place = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    // Якщо знизу тісно, а зверху місця більше — відкриваємо вгору.
    const drop: 'down' | 'up' = below < 240 && r.top > below ? 'up' : 'down';
    setPos({
      top: drop === 'down' ? r.bottom + 2 : r.top - 2,
      left: r.left, width: r.width, drop,
    });
  }, []);

  useLayoutEffect(() => { if (open) place(); }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || popRef.current?.contains(t)) return;
      close();
    };
    // capture: закриваємось ДО того, як сторінка обробить скрол/клік.
    const onScroll = () => place();
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, close, place]);

  // При відкритті — підсвітити поточний пункт і сфокусувати пошук.
  useEffect(() => {
    if (!open) return;
    const idx = shown.findIndex((o) => o.value === value);
    setHi(idx >= 0 ? idx : 0);
    if (withSearch) searchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => { if (query) setHi(0); }, [query]);

  // Тримати підсвічений пункт у полі зору.
  useEffect(() => {
    if (!open) return;
    const el = popRef.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`);
    // scrollIntoView немає у jsdom і у частині старих браузерів — не падаємо.
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [hi, open]);

  const pick = (o: Opt) => {
    if (o.disabled) return;
    onPick(o.value);
    close();
    btnRef.current?.focus();
  };

  const step = (d: number) => {
    if (shown.length === 0) return;
    let i = hi;
    for (let n = 0; n < shown.length; n++) {
      i = (i + d + shown.length) % shown.length;
      if (!shown[i]!.disabled) break;
    }
    setHi(i);
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) {
        e.preventDefault(); setOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); step(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); }
    else if (e.key === 'Home') { e.preventDefault(); setHi(0); }
    else if (e.key === 'End') { e.preventDefault(); setHi(shown.length - 1); }
    else if (e.key === 'Enter' || (e.key === ' ' && !withSearch)) {
      e.preventDefault();
      if (shown[hi]) pick(shown[hi]!);
    } else if (e.key === 'Escape') { e.preventDefault(); close(); btnRef.current?.focus(); }
    else if (e.key === 'Tab') { close(); }
    else if (!withSearch && e.key.length === 1) {
      // Набір символів як у нативному списку: «пр» → «Продаж».
      const now = Date.now();
      const buf = now - typeBuf.current.at < 700 ? typeBuf.current.text + e.key : e.key;
      typeBuf.current = { text: buf, at: now };
      const i = shown.findIndex((o) => o.label.toLowerCase().startsWith(buf.toLowerCase()));
      if (i >= 0) setHi(i);
    }
  };

  let lastGroup: string | null = null;

  return (
    <>
      <button
        type="button" ref={btnRef} id={id} title={title}
        aria-hidden={ariaHidden || undefined}
        className={`nice-select${className ? ` ${className}` : ''}`}
        style={style}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        onKeyDown={onKey}
      >
        <span className={`nice-select__label${selected?.label ? '' : ' is-placeholder'}`}>
          {selected?.label || ' '}
        </span>
        <span className="nice-select__caret" aria-hidden>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.6"
              strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && !disabled && createPortal(
        <div
          ref={popRef}
          id={listId}
          role="listbox"
          className="nice-select__pop"
          style={{
            top: pos.drop === 'down' ? pos.top : undefined,
            bottom: pos.drop === 'up' ? window.innerHeight - pos.top : undefined,
            left: pos.left,
            minWidth: pos.width,
          }}
          onKeyDown={onKey}
        >
          {withSearch && (
            <div className="nice-select__search">
              <input
                ref={searchRef} value={query} placeholder="пошук…"
                onChange={(e) => setQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
          <div className="nice-select__list">
            {shown.length === 0 && (
              <div className="nice-select__empty">Нічого не знайдено</div>
            )}
            {shown.map((o, i) => {
              const head = o.group && o.group !== lastGroup ? o.group : null;
              lastGroup = o.group;
              return (
                <div key={`${o.value}-${i}`}>
                  {head && <div className="nice-select__group">{head}</div>}
                  <div
                    data-i={i}
                    role="option"
                    aria-selected={o.value === value}
                    aria-disabled={o.disabled || undefined}
                    className={'nice-select__opt'
                      + (i === hi ? ' is-hi' : '')
                      + (o.value === value ? ' is-sel' : '')
                      + (o.disabled ? ' is-disabled' : '')}
                    onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                    onMouseEnter={() => !o.disabled && setHi(i)}
                  >
                    {o.label || ' '}
                  </div>
                </div>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
